import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "motion/react";
import { ChevronLeft, ChevronRight, ChevronDown, FilterLines, Eye, EyeOff, ArrowUp, ArrowDown } from "@untitledui/icons";
import type { ActivityFilters, ActivityItem } from "@/types/activity";
import { getCategorySortIndex } from "@/utils/activity-labels";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Carousel, useCarousel } from "@/components/application/carousel/carousel-base";

// ============================================================================
// localStorage helpers
// ============================================================================

const LS_COLLAPSED_KEY = "activity-feed-collapsed-groups";
const LS_ORDER_KEY = "activity-feed-category-order";
const LS_HIDDEN_KEY = "activity-feed-hidden-categories";

function loadJsonSet(key: string): Set<string> {
    try {
        const raw = localStorage.getItem(key);
        if (raw) return new Set(JSON.parse(raw));
    } catch { /* ignore */ }
    return new Set();
}

function saveJsonSet(key: string, set: Set<string>) {
    localStorage.setItem(key, JSON.stringify([...set]));
}

function loadJsonArray(key: string): string[] | null {
    try {
        const raw = localStorage.getItem(key);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.length > 0) return parsed;
        }
    } catch { /* ignore */ }
    return null;
}

function saveJsonArray(key: string, arr: string[]) {
    localStorage.setItem(key, JSON.stringify(arr));
}

// ============================================================================
// Types & constants
// ============================================================================

interface ActivityFeedProps {
    items: ActivityItem[];
    renderItem: (item: ActivityItem) => React.ReactElement | null;
    filters?: ActivityFilters;
    /** Global fallback when per-category is not used */
    onLoadMore?: () => void;
    hasMore?: boolean;
    isLoadingMore?: boolean;
    /** Per-category load more (one fetch per category, 50 items) */
    onLoadMoreForCategory?: (category: string) => void;
    hasMoreForCategory?: (category: string) => boolean;
    isLoadingMoreForCategory?: (category: string) => boolean;
}

interface ActivityGroup {
    groupKey: string;
    label: string;
    items: ActivityItem[];
    category: string;
    maxTimestamp: number;
}

const CAROUSEL_NAV_BUTTON_CLASS =
    "flex shrink-0 items-center justify-center rounded-full border border-secondary bg-primary p-2 shadow-sm transition-colors hover:bg-secondary disabled:pointer-events-none disabled:opacity-50";

const CARD_WIDTH_PX = 350;

// ============================================================================
// Sub-components
// ============================================================================

/** Next: scroll to next slide, or trigger onLoadMore when at last slide and hasMore */
function ActivityFeedNextButton({
    hasMore,
    onLoadMore,
}: {
    hasMore: boolean;
    onLoadMore?: () => void;
}) {
    const { t } = useTranslation(["pages:activity", "common"]);
    const { scrollNext, canScrollNext } = useCarousel();
    const atEnd = !canScrollNext;
    const canLoadMore = atEnd && hasMore && onLoadMore;
    const handleClick = () => {
        if (canScrollNext) scrollNext();
        else if (canLoadMore) onLoadMore!();
    };
    const isDisabled = !canScrollNext && !canLoadMore;
    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={isDisabled}
            className={CAROUSEL_NAV_BUTTON_CLASS}
            aria-label={canLoadMore ? t("activityFeed.loadMoreActivities") : t("activityFeed.nextSlide")}
        >
            <ChevronRight className="size-5 text-primary" />
        </button>
    );
}

function CarouselCounter({ totalItems }: { totalItems: number }) {
    const { selectedIndex, scrollSnaps } = useCarousel();
    if (scrollSnaps.length <= 1) return null;
    return (
        <div className="flex justify-center py-2">
            <span className="text-xs text-quaternary">
                {selectedIndex + 1} / {totalItems}
            </span>
        </div>
    );
}

/** Captures horizontal wheel/trackpad events and scrolls the carousel (no vertical wheel → horizontal) */
function WheelScrollHandler({ children }: { children: React.ReactNode }) {
    const { api } = useCarousel();
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el || !api) return;
        const handler = (e: WheelEvent) => {
            if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
                e.preventDefault();
                if (e.deltaX > 0) api.scrollPrev();
                else api.scrollNext();
            }
        };
        el.addEventListener("wheel", handler, { passive: false });
        return () => el.removeEventListener("wheel", handler);
    }, [api]);

    return <div ref={ref}>{children}</div>;
}

// ============================================================================
// Settings Popover
// ============================================================================

function SettingsPopover({
    categoryOrder,
    hiddenCategories,
    onReorder,
    onToggleVisibility,
}: {
    categoryOrder: string[];
    hiddenCategories: Set<string>;
    onReorder: (newOrder: string[]) => void;
    onToggleVisibility: (category: string) => void;
}) {
    const { t } = useTranslation(["pages:activity", "common"]);
    const [isOpen, setIsOpen] = useState(false);
    const popoverRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;
        const handleClick = (e: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [isOpen]);

    const moveUp = useCallback(
        (index: number) => {
            if (index <= 0) return;
            const next = [...categoryOrder];
            [next[index - 1], next[index]] = [next[index], next[index - 1]];
            onReorder(next);
        },
        [categoryOrder, onReorder]
    );

    const moveDown = useCallback(
        (index: number) => {
            if (index >= categoryOrder.length - 1) return;
            const next = [...categoryOrder];
            [next[index], next[index + 1]] = [next[index + 1], next[index]];
            onReorder(next);
        },
        [categoryOrder, onReorder]
    );

    return (
        <div className="relative" ref={popoverRef}>
            <Button
                size="sm"
                color="secondary"
                onClick={() => setIsOpen(!isOpen)}
                iconLeading={FilterLines}
                aria-label={t("activityFeed.sectionSettings")}
            />

            {isOpen && (
                <div className="absolute right-0 top-full mt-2 z-50 w-64 rounded-lg bg-primary shadow-lg ring-1 ring-secondary p-2">
                    <p className="text-xs font-medium text-tertiary px-2 py-1 mb-1">
                        {t("activityFeed.sectionOrderVisibility")}
                    </p>
                    {categoryOrder.map((cat, index) => {
                        const isHidden = hiddenCategories.has(cat);
                        const label = t(`activityFeed.categories.${cat}`, { defaultValue: cat });
                        return (
                            <div
                                key={cat}
                                className="flex items-center gap-1.5 px-2 py-1.5 rounded-md hover:bg-secondary/50"
                            >
                                <button
                                    className="p-0.5 rounded hover:bg-secondary disabled:opacity-30"
                                    onClick={() => moveUp(index)}
                                    disabled={index === 0}
                                    aria-label={t("activityFeed.moveUp", { label })}
                                >
                                    <ArrowUp className="h-3.5 w-3.5 text-tertiary" />
                                </button>
                                <button
                                    className="p-0.5 rounded hover:bg-secondary disabled:opacity-30"
                                    onClick={() => moveDown(index)}
                                    disabled={index === categoryOrder.length - 1}
                                    aria-label={t("activityFeed.moveDown", { label })}
                                >
                                    <ArrowDown className="h-3.5 w-3.5 text-tertiary" />
                                </button>
                                <span className={`text-sm flex-1 ${isHidden ? "text-tertiary line-through" : "text-primary"}`}>
                                    {label}
                                </span>
                                <button
                                    className="p-0.5 rounded hover:bg-secondary"
                                    onClick={() => onToggleVisibility(cat)}
                                    aria-label={`${isHidden ? t("activityFeed.show") : t("activityFeed.hide")} ${label}`}
                                >
                                    {isHidden ? (
                                        <EyeOff className="h-3.5 w-3.5 text-tertiary" />
                                    ) : (
                                        <Eye className="h-3.5 w-3.5 text-tertiary" />
                                    )}
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ============================================================================
// Main Component
// ============================================================================

export const ActivityFeed = ({
    items,
    renderItem,
    filters,
    onLoadMore,
    hasMore = false,
    isLoadingMore = false,
    onLoadMoreForCategory,
    hasMoreForCategory,
    isLoadingMoreForCategory,
}: ActivityFeedProps) => {
    const { t } = useTranslation(["pages:activity", "common"]);

    // Collapse / order / hidden state (persisted in localStorage)
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
        () => loadJsonSet(LS_COLLAPSED_KEY)
    );
    const [userCategoryOrder, setUserCategoryOrder] = useState<string[] | null>(
        () => loadJsonArray(LS_ORDER_KEY)
    );
    const [hiddenCategories, setHiddenCategories] = useState<Set<string>>(
        () => loadJsonSet(LS_HIDDEN_KEY)
    );

    const toggleCollapse = useCallback((groupKey: string) => {
        setCollapsedGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupKey)) next.delete(groupKey);
            else next.add(groupKey);
            saveJsonSet(LS_COLLAPSED_KEY, next);
            return next;
        });
    }, []);

    const handleReorder = useCallback((newOrder: string[]) => {
        setUserCategoryOrder(newOrder);
        saveJsonArray(LS_ORDER_KEY, newOrder);
    }, []);

    const handleToggleVisibility = useCallback((cat: string) => {
        setHiddenCategories(prev => {
            const next = new Set(prev);
            if (next.has(cat)) next.delete(cat);
            else next.add(cat);
            saveJsonSet(LS_HIDDEN_KEY, next);
            return next;
        });
    }, []);

    const filtered = useMemo(() => {
        let list = [...items];
        if (filters?.readState && filters.readState !== "all") {
            list = list.filter((i) => (filters.readState === "unread" ? !i.isRead : i.isRead));
        }
        if (filters?.fromDate) {
            list = list.filter((i) => i.timestamp >= filters.fromDate!);
        }
        if (filters?.toDate) {
            list = list.filter((i) => i.timestamp <= filters.toDate!);
        }
        if (filters?.keyword) {
            const k = filters.keyword.toLowerCase();
            list = list.filter((i) => {
                if (i.title?.toLowerCase().includes(k)) return true;
                if (i.description?.toLowerCase().includes(k)) return true;
                if (i.metadata?.containerNumber?.toLowerCase().includes(k)) return true;
                if (i.metadata?.containerId?.toLowerCase().includes(k)) return true;
                if (i.metadata?.documentType?.toLowerCase().includes(k)) return true;
                if (i.metadata?.routeFrom?.toLowerCase().includes(k)) return true;
                if (i.metadata?.routeTo?.toLowerCase().includes(k)) return true;
                if (i.metadata?.shippingCompany?.toLowerCase().includes(k)) return true;
                if (i.metadata?.commodity?.toLowerCase().includes(k)) return true;
                if (i.attachments?.some((att) => att.name?.toLowerCase().includes(k))) return true;
                return false;
            });
        }
        if (filters?.activityCategories && filters.activityCategories.length > 0) {
            list = list.filter(
                (i) => i.activityCategory && filters.activityCategories!.includes(i.activityCategory)
            );
        }
        list.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        return list;
    }, [items, filters]);

    const orderedGroups = useMemo((): ActivityGroup[] => {
        const map = new Map<string, ActivityItem[]>();
        // Map legacy categories to new ones
        const legacyCategoryMap: Record<string, string> = {
            validation: "document",
            expedition: "tracking",
            notification: "agent",
            workflow: "agent",
        };
        for (const i of filtered) {
            const rawCategory = (i.activityCategory ?? "other").toLowerCase();
            const category = legacyCategoryMap[rawCategory] || rawCategory;
            // Group by category ONLY (one carousel section per category)
            const groupKey = category;
            const arr = map.get(groupKey) ?? [];
            arr.push(i);
            map.set(groupKey, arr);
        }
        const groups: ActivityGroup[] = [];
        map.forEach((arr, groupKey) => {
            const category = groupKey;
            const maxTimestamp = Math.max(...arr.map((a) => a.timestamp.getTime()));
            groups.push({
                groupKey,
                label: t(`activityFeed.categories.${category}`, { defaultValue: category }),
                items: arr,
                category,
                maxTimestamp,
            });
        });

        // Sort: user order if available, otherwise default category sort
        if (userCategoryOrder) {
            groups.sort((a, b) => {
                const idxA = userCategoryOrder.indexOf(a.category);
                const idxB = userCategoryOrder.indexOf(b.category);
                const orderA = idxA >= 0 ? idxA : userCategoryOrder.length;
                const orderB = idxB >= 0 ? idxB : userCategoryOrder.length;
                if (orderA !== orderB) return orderA - orderB;
                return b.maxTimestamp - a.maxTimestamp;
            });
        } else {
            groups.sort((a, b) => {
                const orderA = getCategorySortIndex(a.category);
                const orderB = getCategorySortIndex(b.category);
                if (orderA !== orderB) return orderA - orderB;
                return b.maxTimestamp - a.maxTimestamp;
            });
        }
        return groups;
    }, [filtered, userCategoryOrder, t]);

    // Unique categories present in current data (for settings popover)
    const presentCategories = useMemo(() => {
        const cats: string[] = [];
        const seen = new Set<string>();
        for (const g of orderedGroups) {
            if (!seen.has(g.category)) {
                seen.add(g.category);
                cats.push(g.category);
            }
        }
        return cats;
    }, [orderedGroups]);

    // Visible groups (respecting hidden categories)
    const visibleGroups = useMemo(
        () => orderedGroups.filter(g => !hiddenCategories.has(g.category)),
        [orderedGroups, hiddenCategories]
    );

    if (filtered.length === 0) {
        return (
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary text-tertiary">
                {t("activityFeed.noActivities")}
            </div>
        );
    }

    return (
        <>
            {/* Settings bar */}
            <div className="flex items-center justify-end mb-2">
                <SettingsPopover
                    categoryOrder={presentCategories}
                    hiddenCategories={hiddenCategories}
                    onReorder={handleReorder}
                    onToggleVisibility={handleToggleVisibility}
                />
            </div>

            <div className="rounded-xl bg-primary p-0 shadow-xs ring-1 ring-secondary">
                <ul className="divide-y divide-secondary">
                    {visibleGroups.map((group) => {
                        const isCollapsed = collapsedGroups.has(group.groupKey);
                        return (
                            <li key={group.groupKey} className="p-0">
                                {/* Collapsible header */}
                                <div
                                    className="sticky top-0 z-10 border-b border-secondary bg-primary/80 px-6 py-3 backdrop-blur cursor-pointer select-none flex items-center gap-3"
                                    onClick={() => toggleCollapse(group.groupKey)}
                                    role="button"
                                    tabIndex={0}
                                    aria-expanded={!isCollapsed}
                                    aria-label={isCollapsed ? t("activityFeed.expandSection", { label: group.label }) : t("activityFeed.collapseSection", { label: group.label })}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            toggleCollapse(group.groupKey);
                                        }
                                    }}
                                >
                                    <span className="text-sm font-medium text-tertiary flex-1">
                                        {group.label}
                                    </span>
                                    <Badge color="gray" size="sm">
                                        {group.items.length}
                                    </Badge>
                                    <motion.span
                                        animate={{ rotate: isCollapsed ? -90 : 0 }}
                                        transition={{ duration: 0.15 }}
                                    >
                                        <ChevronDown className="h-4 w-4 text-tertiary" />
                                    </motion.span>
                                </div>

                                {/* Collapsible carousel content */}
                                <AnimatePresence initial={false}>
                                    {!isCollapsed && (
                                        <motion.div
                                            key="carousel-content"
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2, ease: "easeInOut" }}
                                            className="overflow-hidden"
                                        >
                                            <div className="px-4 py-4 sm:px-6">
                                                <Carousel.Root
                                                    opts={{
                                                        align: "start",
                                                        loop: false,
                                                        skipSnaps: false,
                                                        dragFree: true,
                                                    }}
                                                >
                                                    <WheelScrollHandler>
                                                    <div className="flex items-stretch gap-2">
                                                        <div className="relative flex shrink-0 items-center">
                                                            <Carousel.PrevTrigger className={CAROUSEL_NAV_BUTTON_CLASS}>
                                                                <ChevronLeft className="size-5 text-primary" />
                                                            </Carousel.PrevTrigger>
                                                        </div>
                                                        <Carousel.Content className="min-w-0 flex-1 overflow-hidden">
                                                            {group.items.map((item) => (
                                                                <Carousel.Item
                                                                    key={item.id}
                                                                    className="shrink-0 mr-4"
                                                                    style={{
                                                                        width: CARD_WIDTH_PX,
                                                                        minWidth: CARD_WIDTH_PX,
                                                                        flexBasis: CARD_WIDTH_PX,
                                                                    }}
                                                                >
                                                                    <div className="w-full">
                                                                        {renderItem(item)}
                                                                    </div>
                                                                </Carousel.Item>
                                                            ))}
                                                        </Carousel.Content>
                                                        <div className="relative flex shrink-0 items-center">
                                                            {isLoadingMoreForCategory?.(group.category) && (
                                                                <div className="absolute -left-8 top-1/2 h-5 w-5 -translate-y-1/2 animate-spin rounded-full border-2 border-secondary border-t-brand-solid" />
                                                            )}
                                                            <ActivityFeedNextButton
                                                                hasMore={
                                                                    hasMoreForCategory
                                                                        ? hasMoreForCategory(group.category)
                                                                        : hasMore
                                                                }
                                                                onLoadMore={
                                                                    onLoadMoreForCategory
                                                                        ? () => onLoadMoreForCategory(group.category)
                                                                        : onLoadMore
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                    </WheelScrollHandler>
                                                    <CarouselCounter totalItems={group.items.length} />
                                                </Carousel.Root>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </li>
                        );
                    })}
                </ul>
            </div>

            {isLoadingMore && (
                <div className="flex justify-center py-4">
                    <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-brand-secondary" />
                </div>
            )}

            {!hasMore && filtered.length > 0 && (
                <div className="py-4 text-center text-sm text-tertiary">
                    {t("activityFeed.noMoreToLoad")}
                </div>
            )}
        </>
    );
};

export default ActivityFeed;
